import { Css, Palette } from "@homebound/beam";
import { FileList } from "src/components/FileList";
import { Icon } from "src/components/Icon";
import { DocumentEditorDetailFragment } from "src/fragments/circularFragments.generated";
import { DocumentType, SaveDocumentInput } from "src/generated/graphqlTypes";
import { useSaveDocumentsMutation } from "src/routes/projects/documents/DocumentsPage.generated";
import { bytesToSize, getIconKeyByFileName } from "src/utils/files";
import { UppyUploader } from "./UppyUploader";

export type DocumentUploaderProps = {
  documentType: DocumentType;
  file: DocumentEditorDetailFragment | undefined;
  onFinish: (file: DocumentEditorDetailFragment | undefined) => void;
  projectId: string;
  message?: string;
  readOnly?: boolean;
  error?: string;
  onClear?: () => void;
  multiple?: boolean;
};

/**
 * Document-specific file uploader that uploads files via UppyUploader and creates
 * documents via the `saveDocuments` mutation.
 *
 * When uploading multiple documents, `onFinish` is called once for each file uploaded.
 *
 * Usage chain:
 * - Only consumed by {@link BoundFileUploader} (src/components/formFields/BoundFileUploader.tsx)
 * - `BoundFileUploader` is used by:
 *   - `CreateContractModal` (src/routes/projects/estimates/CreateContractModal.tsx) — uploads executed agreements
 *   - `FormFileUploaderSection` (src/components/contracts/FormFileUploaderSection.tsx) — reusable file upload section
 * - `FormFileUploaderSection` is used by:
 *   - `DetailsEditor` (src/routes/projects/homeowner-contracts/DetailsEditor.tsx) — homeowner contract uploads
 *   - `CommitmentChangeOrderEditor` (src/routes/projects/commitments/CommitmentChangeOrderEditor.tsx) — trade change order uploads
 */
export function DocumentUploader(props: DocumentUploaderProps) {
  const { documentType, projectId, onFinish, file, message, readOnly, multiple } = props;

  const [saveDocuments] = useSaveDocumentsMutation();

  if (readOnly) {
    return (
      <div css={Css.df.aic.w100.hPx(72).br8.$} data-testid="read-only-file-uploader">
        {file ? <FileList files={[file]} /> : "No files uploaded"}
      </div>
    );
  }

  // If a file is already uploaded, show it with a clear option
  if (file) {
    const clearFile = props.onClear ?? (() => onFinish(undefined));
    return (
      <div css={Css.df.aic.w100.hPx(72).br8.ba.bcGray400.bgGray100.$} data-testid="uploaded-file">
        <Icon icon={getIconKeyByFileName(file.name || "file")} inc={4} />
        <div css={Css.ml1.w("inherit").$}>
          <div data-testid="file-name">{file.name}</div>
          {file.sizeInBytes && (
            <div css={Css.xs2Sb.$} data-testid="file-caption">
              {bytesToSize(file.sizeInBytes)}
            </div>
          )}
        </div>
        <div css={Css.cursorPointer.mla.mr1.$} onClick={clearFile} data-testid="file-uploader-clear">
          <Icon icon="trash" inc={4} color={Palette.Gray700} />
        </div>
      </div>
    );
  }

  return (
    <UppyUploader
      type="DragDrop"
      allowedFileTypes={["application/pdf"]}
      dragDropText={message ?? "Drag & drop files"}
      dragDropWidth="100%"
      dragDropHeight={72}
      maxNumberOfFiles={multiple ? undefined : 1}
      errorMessage={props.error}
      onFinish={async (uppyFile) => {
        const { s3Key } = uppyFile.meta;
        const input: SaveDocumentInput = {
          parentId: projectId,
          documentType,
          name: uppyFile.name,
          sizeInBytes: uppyFile.size ?? 0,
          asset: {
            contentType: uppyFile.type ?? "application/pdf",
            fileName: uppyFile.name,
            s3Key,
            fileSizeInBytes: uppyFile.size ?? 0,
          },
        };
        const { data } = await saveDocuments({ variables: { input } });
        onFinish(data?.saveDocuments.documents.first);
      }}
    />
  );
}
